import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MessagingOverview as MessagingOverviewType } from '@/api/billing/billing.types';
import { MessageSquare, Euro, Hash, TrendingUp, Bot, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessagingOverviewProps {
  messagingOverview: MessagingOverviewType | null;
  isLoading: boolean;
  onError: (error: string) => void;
  className?: string;
}

export const MessagingOverview = ({ messagingOverview, isLoading, className }: MessagingOverviewProps) => {
  const formatCurrency = (amount: number) => `€${amount.toFixed(2)}`;

  if (isLoading) {
    return (
      <Card className={cn("h-full flex flex-col", className)}>
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <MessageSquare className="h-4 w-4 text-orange-500" />
              </div>
              <Skeleton className="h-5 w-40" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0">
          {/* Summary skeleton */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="p-3 rounded-lg border space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-5 w-12" />
              </div>
            ))}
          </div>
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                  <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-12" />
                    </div>
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    );
  }

  if (!messagingOverview?.agents || messagingOverview.agents.length === 0) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500/10">
              <MessageSquare className="h-4 w-4 text-orange-500" />
            </div>
            <div className="space-y-0.5">
              <CardTitle className="text-base leading-none">Messaging Overview</CardTitle>
              <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                Session breakdown per agent
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[calc(100%-73px)]">
          <div className="text-center space-y-3">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">No Messaging Data</p>
              <p className="text-xs text-muted-foreground">Messaging data will appear here once chat sessions begin</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { summary, agents } = messagingOverview;

  const getRankColor = (index: number) => {
    switch (index) {
      case 0: return 'hsl(24 95% 53%)'; // orange
      case 1: return 'hsl(39 84% 56%)'; // amber
      case 2: return 'hsl(217 91% 60%)'; // blue
      default: return 'hsl(var(--muted-foreground))';
    }
  };

  const getAgentTypeIcon = (agentType: string) => {
    switch (agentType) {
      case 'squad': return Users;
      default: return Bot;
    }
  };

  const getAgentTypeLabel = (agentType: string) => {
    switch (agentType) {
      case 'assistant': return 'Assistant';
      case 'squad': return 'Squad';
      default: return 'Unknown';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay: 0.15 }}
      className={className}
    >
      <Card className="h-full flex flex-col shadow-sm">
        <CardHeader className="pb-3 flex-shrink-0 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500/10">
                <MessageSquare className="h-4 w-4 text-orange-500" />
              </div>
              <div className="space-y-0.5">
                <CardTitle className="text-base leading-none">Messaging Overview</CardTitle>
                <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                  Session breakdown per agent
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="flex items-center gap-1 text-xs">
              <TrendingUp className="h-3 w-3" />
              By Cost
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col min-h-0 p-0">
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3 px-6 pt-4 pb-3">
            <div className="p-2.5 rounded-lg border bg-muted/30 text-center">
              <p className="text-xs text-muted-foreground mb-0.5">Sessions</p>
              <p className="text-sm font-semibold">{summary.totalSessions}</p>
            </div>
            <div className="p-2.5 rounded-lg border bg-muted/30 text-center">
              <p className="text-xs text-muted-foreground mb-0.5">Per Session</p>
              <p className="text-sm font-semibold">{formatCurrency(summary.pricePerSession)}</p>
            </div>
            <div className="p-2.5 rounded-lg border bg-muted/30 text-center">
              <p className="text-xs text-muted-foreground mb-0.5">Total Cost</p>
              <p className="text-sm font-semibold">{formatCurrency(summary.totalCost)}</p>
            </div>
          </div>

          {/* Scrollable agent list */}
          <ScrollArea className="flex-1 px-6 pb-4">
            <div className="space-y-3 pr-2">
              {agents.map((agent, index) => {
                const rankColor = getRankColor(index);
                const AgentIcon = getAgentTypeIcon(agent.agentType);

                return (
                  <motion.div
                    key={agent.agentId}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02, duration: 0.2, ease: "easeOut" }}
                    className="group relative overflow-hidden rounded-lg border bg-card p-3 hover:bg-muted/30 hover:shadow-sm transition-all duration-200"
                  >
                    <div className="flex items-center gap-3">
                      {/* Rank indicator */}
                      <div className="flex-shrink-0">
                        <div
                          className="flex items-center justify-center w-8 h-8 rounded-md font-semibold text-xs"
                          style={{
                            backgroundColor: `${rankColor}15`,
                            color: rankColor,
                            border: `1px solid ${rankColor}20`
                          }}
                        >
                          #{index + 1}
                        </div>
                      </div>

                      {/* Agent info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1.5">
                          <h4 className="font-medium text-sm truncate">{agent.agentName}</h4>
                          <div className="flex items-center gap-1 text-xs font-semibold">
                            <Euro className="h-3 w-3 text-muted-foreground" />
                            <span>{agent.cost.toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <AgentIcon className="h-3 w-3" />
                            <span>{getAgentTypeLabel(agent.agentType)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            <span>{agent.sessions} sessions</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </motion.div>
  );
};
